import axios from "axios";
import {
  NGROK_SKIP_HEADER,
  LOGIN_URL,
  PROXY_DISCORD_URL,
  MARKET_CALENDAR_URL,
  QUERY_PARAMS,
  NUM_OF_CHARTS,
} from "./constants.js";

function normalizeNumOfCharts(value) {
  return value === NUM_OF_CHARTS.TWO || value === NUM_OF_CHARTS.FOUR ? value : null;
}

// The layout is sticky across visits: it lives in localStorage and can be
// overridden for a single load with the numOfCharts query param. The param is
// never written back to the url -- the store is the source of truth.
export function getStoredNumOfCharts() {
  return (
    normalizeNumOfCharts(localStorage.getItem(QUERY_PARAMS.NUM_OF_CHARTS)) || NUM_OF_CHARTS.TWO
  );
}

export function setStoredNumOfCharts(numOfCharts) {
  const effective = normalizeNumOfCharts(numOfCharts) || NUM_OF_CHARTS.TWO;
  localStorage.setItem(QUERY_PARAMS.NUM_OF_CHARTS, effective);
  return effective;
}

export function getEffectiveNumOfCharts() {
  const query = new URLSearchParams(window.location.search);
  const passed = normalizeNumOfCharts(query.get(QUERY_PARAMS.NUM_OF_CHARTS));
  return passed || getStoredNumOfCharts();
}

export function login(token) {
  return axios({
    method: "post",
    url: LOGIN_URL,
    headers: NGROK_SKIP_HEADER,
    data: { access_token: token },
    withCredentials: true,
  })
    .then((res) => res.data)
    .then((res) => res.is_authorized)
    .catch((e) => false);
}

export function getHeaders() {
  const result = {};
  result[NGROK_SKIP_HEADER] = true;
  return result;
}

export function getMarketCalendar() {
  return axios({
    method: "get",
    url: MARKET_CALENDAR_URL,
    headers: getHeaders(),
    withCredentials: true,
  }).then(res => {
    const marketCalendar = typeof res.data === 'object' ? res.data : {};
    return marketCalendar;
  })
}

function getHourAndMinuteInEST(date = undefined) {
  const weekdayOptions = {
    timeZone: "America/New_York",
    weekday: "short",
  };
  const hourOptions = {
    hour: "numeric",
    hour12: false,
    timeZone: "America/New_York",
  };
  const minuteOptions = {
    minute: "numeric",
    hour12: false,
    timeZone: "America/New_York",
  };
  const now = date || new Date();
  const hour = parseInt(now.toLocaleString("en-US", hourOptions));
  return {
    weekday: now.toLocaleString("en-US", weekdayOptions),
    hour: hour === 24 ? 0 : hour,
    minute: parseInt(now.toLocaleString("en-US", minuteOptions)),
  };
}

function isWeekend(weekday) {
  return weekday === "Sat" || weekday === "Sun";
}

function isPreMarket(hour, minute) {
  const cur = hour * 60 + minute;
  return cur < 9 * 60 + 30;
}

function isPostMarket(hour, minute) {
  const cur = hour * 60 + minute;
  return cur > 16 * 60;
}

function getESTDateString(date = new Date()) {
  const options = { timeZone: "America/New_York" };
  
  const year = date.toLocaleString("en-US", { ...options, year: "numeric" });
  const month = date.toLocaleString("en-US", { ...options, month: "2-digit" });
  const day = date.toLocaleString("en-US", { ...options, day: "2-digit" });

  return `${year}-${month}-${day}`;
}

export function isMarketOpenOnDate(marketCalendar = {}, date) {
  const { weekday } = getHourAndMinuteInEST(date);
  if (isWeekend(weekday)) {
    return false;
  }

  const dateStr = getESTDateString(date);
  const specialDay = marketCalendar[dateStr];

  if (specialDay && specialDay.status === "closed") {
    return false;
  }

  return true;
}

export default function isDuringMarketTime(marketCalendar = {}, date = undefined) {
  const now = date || new Date();
  const { weekday, hour, minute } = getHourAndMinuteInEST(now);
  if (isWeekend(weekday)) {
    return false;
  }

  const dateStr = getESTDateString(now);
  const specialDay = marketCalendar[dateStr];

  if (specialDay) {
    if (specialDay.status === "closed") {
      return false;
    }

    if (specialDay.status === "early-close" && specialDay.close) {
      const closeTime = new Date(specialDay.close);
      return !isPreMarket(hour, minute) && now < closeTime;
    }
  }

  if (isPreMarket(hour, minute)) {
    return false;
  }
  if (isPostMarket(hour, minute)) {
    return false;
  }
  return true;
}

export function isWeekendInEST(date) {
  const { weekday } = getHourAndMinuteInEST(date);
  return isWeekend(weekday);
}

export function getMostRecentMarketDate(marketCalendar) {
  const now = new Date();
  
  let searchDate = new Date(now.getTime());

  const { hour, minute } = getHourAndMinuteInEST(now);
  const isTodayOpen = isMarketOpenOnDate(marketCalendar, searchDate);

  if (!isTodayOpen || isPreMarket(hour, minute)) {
    searchDate.setDate(searchDate.getDate() - 1);
  }

  while (!isMarketOpenOnDate(marketCalendar, searchDate)) {
    searchDate.setDate(searchDate.getDate() - 1);
  }

  return searchDate;
}

export function convertDateToEST(date) {
  const year = date.toLocaleString("en-US", {
    year: "numeric",
    timeZone: "America/New_York",
  });
  const month = date.toLocaleString("en-US", {
    month: "short",
    timeZone: "America/New_York",
  });
  const day = date.toLocaleString("en-US", {
    day: "numeric",
    timeZone: "America/New_York",
  });
  return { year, month, day };
}

export function convertDateToESTString(date) {
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    timeZone: "America/New_York",
  });
}

export function getTimezoneDiffWithEST() {
  const now = new Date();

  const nyString = now.toLocaleString("en-US", { timeZone: "America/New_York" });
  const nyDate = new Date(nyString);

  const localString = now.toLocaleString("en-US");
  const localDate = new Date(localString);

  return nyDate - localDate;
}

export function getEndOfDayInNewYork(date) {
  const year = parseInt(date.toLocaleString("en-US", { year: "numeric", timeZone: "America/New_York" }), 10);
  const month = parseInt(date.toLocaleString("en-US", { month: "2-digit", timeZone: "America/New_York" }), 10);
  const day = parseInt(date.toLocaleString("en-US", { day: "2-digit", timeZone: "America/New_York" }), 10);
  const utc23 = Date.UTC(year, month - 1, day, 23, 59, 59, 999);
  const nyTimeStr = new Date(utc23).toLocaleString("en-US", {
    timeZone: "America/New_York",
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const [nyHour, nyMin, nySec] = nyTimeStr.split(":").map(Number);
  const diffMs =
    (23 - nyHour) * 60 * 60 * 1000 +
    (59 - nyMin) * 60 * 1000 +
    (59 - nySec) * 1000;
  return new Date(utc23 + diffMs);
}

export function bufferToBase64(buffer) {
  var binary = "";
  const bytes = new Uint8Array(buffer);
  for (var i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export function bufferToBase64Image(buffer) {
  var binary = "";
  const bytes = new Uint8Array(buffer);
  for (var i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const chars = window.btoa(binary);
  return `data:image/png;base64,${chars}`;
}

export function sendProxyDiscordRequest(url, params = undefined, isImage = undefined) {
  return axios({
    method: "post",
    url: PROXY_DISCORD_URL,
    data: { url, params, is_image: isImage },
    headers: getHeaders(),
    responseType: isImage ? "arraybuffer" : undefined,
    withCredentials: true,
  });
}
